import * as React from 'react';
import { useEffect } from 'react';

import classNames from 'classnames';

import DateTimeUtils from 'js/utils/DateTimeUtils';

import { type EventData, useTracker } from '@coursera/event-pulse/react';

import type { Notification as NotificationType } from 'bundles/notification-center/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import 'css!./__styles__/Notification';

type TrackNotificationProps = EventData<'view_notification' | 'click_notification'>;

type Props = {
  href: string;
  children: React.ReactNode;
  notification: NotificationType;
  rank?: number;
  trackingData?: Omit<TrackNotificationProps, 'notification'>;
  onClick: (notification: NotificationType) => void;
};

// TODO: Limit length of notification text
const Notification: React.FC<Props> = ({ href, notification, rank, onClick, trackingData, children }) => {
  const { messageType, id, isRead } = notification;
  const eventingData: TrackNotificationProps = {
    notification: {
      templateId: messageType,
      channelType: 'notification_center' as const,
      id,
      rank,
      isPreviouslyRead: isRead,
      redirectUrl: href,
    },
    ...(trackingData || {}),
  };
  const track = useTracker();

  /* Cannot use visibility tracker as TrackedLink2 does not accept ref */
  useEffect(() => {
    track('view_notification', eventingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  const handleClick = () => {
    track('click_notification', eventingData);
    onClick(notification);
  };

  return (
    <TrackedLink2
      href={href}
      trackingName="notification"
      data={{
        id: notification.id,
        isRead: notification.isRead,
        createdAt: notification.createdAt,
        messageType: notification.messageType,
      }}
      onClick={handleClick}
      data-testid="notification"
      className={classNames('rc-Notification', { read: notification.isRead })}
    >
      {children}

      <div className="notification-timestamp">
        {DateTimeUtils.momentWithUserTimezone(notification.createdAt).fromNow()}
      </div>
    </TrackedLink2>
  );
};

export default Notification;
